import { config } from "@noted/configuration";
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  OnboardingStateType,
  useAgendaOnboarding,
} from "~/app/scheduling/agenda/use-agenda-onboarding";
import { AccountContext } from "~/components/account-context/account.context";
import { FeatureFlag, useFeatures } from "~/components/features.context";
import {
  AccessOverrides,
  Agenda,
  Auditing,
  Availability,
  Clients,
  ClientSearch,
  Clipboard,
  Dashboard,
  GraphFile,
  Group,
  Invoicing,
  Logout,
  Organisation,
  PrimhdRecord,
  PrimhdReferral,
  PurchaseOrders,
  Reporting,
  Roles,
  Subscription,
  Support,
  TeamAdmin,
  Teams,
  TrackingForms,
  Users,
  Whanau,
  WhanaungaAwhina,
} from "~/components/icons";
import { NavigableListItem } from "~/components/navigable-list/navigable-list";
import { NavLink, NavLinkAnchor } from "~/components/navigation/nav-link";
import { UnescapedTFunction, useI18n } from "~/hooks/use-i18n";
import { IOrganisation } from "~/types";

import { ApplicationsSwitcher } from "./app-switcher";
import { NavigationContext } from "./navigation.context";
import { Sidebar, SidebarExpandButton, SidebarOverlay } from "./sidebar";

const getActiveNavDefinition = (
  t: UnescapedTFunction,
  pathname: string,
  isUpgradable: boolean,
  state: OnboardingStateType,
  featureEnabled: (key: FeatureFlag) => boolean,
  organisation: IOrganisation
) => {
  if (pathname.startsWith("/admin")) {
    return {
      key: "admin",
      label: t("administration_app"),
      links: [
        {
          icon: ClientSearch,
          to: "/admin/clients",
          title: t("clients_admin"),
        },
        {
          icon: Users,
          to: "/admin/users",
          title: t("navigation_labels.users"),
        },
        {
          icon: Roles,
          to: "/admin/roles",
          title: t("navigation_labels.roles"),
        },
        { icon: Teams, to: "/admin/teams", title: t("navigation_labels.teams") },
        {
          icon: Organisation,
          to: "/admin/organisation-settings",
          title: t("organisation_settings"),
        },
        { icon: Clipboard, to: "/admin/client-settings", title: t("client_settings") },
        {
          icon: Subscription,
          to: "/admin/subscription",
          title: t("navigation_labels.subscription"),
          condition: !isUpgradable,
        },
        {
          icon: TrackingForms,
          to: "/admin/tracking-forms",
          title: t("admin_tracking_forms"),
        },
      ],
    };
  }
  if (pathname.startsWith("/scheduling")) {
    return {
      key: "scheduling",
      label: t("scheduling_app"),
      links: [
        ...(state === "ready"
          ? [
              {
                icon: Agenda,
                to: "/scheduling/booking",
                title: t("scheduling_agenda"),
              },
            ]
          : []),
        {
          icon: Availability,
          to: "/scheduling/availability",
          title: t("scheduling_availability"),
        },
      ],
    };
  }
  if (pathname.startsWith("/primhd")) {
    return {
      key: "primhd",
      label: t("primhd_app"),
      links: [
        {
          icon: PrimhdReferral,
          to: "/primhd/referrals",
          title: t("navigation_labels.primhd_referrals"),
        },
        {
          icon: PrimhdRecord,
          to: "/primhd/records",
          title: t("navigation_labels.primhd_records"),
        },
      ],
    };
  }
  if (pathname.startsWith("/auditing")) {
    return {
      key: "auditing",
      label: t("navigation_labels.auditing"),
      links: [
        {
          icon: Auditing,
          to: "/auditing/log",
          title: t("navigation_labels.auditing"),
        },
      ],
    };
  }
  if (pathname.startsWith("/n/acc")) {
    return {
      key: "fragor-acc",
      label: "ACC billing",
      links: [
        {
          icon: Invoicing,
          to: "/n/acc/invoicing",
          title: t("nav_title.invoicing"),
        },
        {
          icon: PurchaseOrders,
          to: "/n/acc/purchase-orders",
          title: t("nav_title.purchase_orders"),
          condition:
            featureEnabled(FeatureFlag.AccPurchaseOrders) &&
            organisation?.accDetails?.purchaseOrdersEnabled,
        },
      ],
    };
  }
  if (pathname.startsWith("/n/reports") || pathname.startsWith("/n/extract")) {
    return {
      key: "reporting",
      label: "Reporting",
      links: [
        {
          icon: GraphFile,
          to: "/n/reports",
          title: t("nav_title.reports"),
          condition: featureEnabled(FeatureFlag.Reports),
        },
        {
          icon: Reporting,
          to: "/n/extract",
          title: t("navigation_labels.extract"),
          condition: featureEnabled(FeatureFlag.Extracts),
        },
      ],
    };
  }
  if (
    pathname.startsWith("/n") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/user-settings")
  ) {
    return {
      key: "clients",
      label: "Client records",
      links: [
        { icon: Dashboard, to: "/dashboard", title: t("nav_title.dashboard") },
        { icon: Clients, to: "/n/clients", title: t("nav_title.clients") },
        {
          icon: Group,
          to: "/n/groups",
          title: t("nav_title.groups"),
          condition: featureEnabled(FeatureFlag.Groups),
        },
        {
          icon: Whanau,
          to: "/n/whanau",
          title: t("nav_title.whanau"),
          condition: featureEnabled(FeatureFlag.Whanau),
        },
        {
          icon: WhanaungaAwhina,
          to: "/n/whanaunga-awhina",
          title: t("nav_title.whanaunga_awhina"),
          condition: featureEnabled(FeatureFlag.WhanaungaAwhina),
        },
        {
          icon: AccessOverrides,
          to: "/n/overrides",
          title: t("nav_title.overrides"),
          condition: featureEnabled(FeatureFlag.UserAccessControl),
        },
        {
          icon: TeamAdmin,
          to: "/n/team-admin",
          title: t("nav_title.team_admin"),
          condition: featureEnabled(FeatureFlag.TeamAdministration),
        },
      ],
    };
  }
  return null;
};

const Navigation = () => {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const { open, setOpen } = useContext(NavigationContext);
  const { organisation, account, isUpgradable } = useContext(AccountContext);
  const isWorker = account.securityRoles.some(s => s.key === "WORKER");
  const { state } = useAgendaOnboarding();
  const { featureEnabled } = useFeatures();

  const [desktopExpanded, desktopExpanderSetterFn] = useState(
    !(localStorage.getItem("ngStorage-mainNavExpanded") === "false")
  );

  const setDesktopExpanded = (expanded: boolean) => {
    desktopExpanderSetterFn(expanded);
    localStorage.setItem("ngStorage-mainNavExpanded", String(expanded));
  };

  const activeItem = getActiveNavDefinition(
    t,
    pathname,
    isUpgradable,
    state,
    featureEnabled,
    organisation
  );
  const onMobileNavClick = () => setOpen(!open);

  return (
    <>
      <SidebarOverlay />
      <Sidebar desktopExpanded={desktopExpanded} open={open}>
        <ul className="flex h-full flex-col" aria-label={t("main_nav")} role="menubar">
          {isWorker && (
            <li role="none" className="p-3">
              <ApplicationsSwitcher role="menuitem" sideBarCollapsed={!desktopExpanded} />
            </li>
          )}
          {activeItem && (
            <li role="none">
              <ul role="menu" aria-label={`${activeItem.label} ${t("sub_nav")}`}>
                {activeItem.links.map(link => {
                  if (link.condition === false) {
                    return null;
                  }
                  return (
                    <NavigableListItem key={link.to} role="none">
                      <NavLink
                        role="menuitem"
                        desktopExpanded={desktopExpanded}
                        onClick={onMobileNavClick}
                        icon={link.icon}
                        to={link.to}
                        title={link.title}
                      />
                    </NavigableListItem>
                  );
                })}
              </ul>
            </li>
          )}
          <NavigableListItem role="none" className="mt-auto">
            <NavLinkAnchor
              href={config.supportPageUrl}
              target="_blank"
              role="menuitem"
              desktopExpanded={desktopExpanded}
              icon={Support}
              title={t("support")}
              onClick={onMobileNavClick}
            />
          </NavigableListItem>
          <NavigableListItem role="none">
            <NavLink
              desktopExpanded={desktopExpanded}
              role="menuitem"
              icon={Logout}
              to="/logout"
              title={t("logout")}
              onClick={onMobileNavClick}
            />
          </NavigableListItem>
        </ul>
        <SidebarExpandButton
          desktopExpanded={desktopExpanded}
          setDesktopExpanded={setDesktopExpanded}
        />
      </Sidebar>
    </>
  );
};

export default Navigation;
